// src/pages/WomenFundPage.js

import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { message, Spin } from "antd";
import {
  CheckCircleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  InformationCircleIcon,
  LockClosedIcon,
  UserIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";

import "../components/Walletview.css";

const WomenFundPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { wallet, baitcashAccount, baitcashBalance } =
    location.state || {};

  const [reasonForFunds, setReasonForFunds] = useState("");
  const [profession, setProfession] = useState("");
  const [amount, setAmount] = useState("");
  const [cvFile, setCvFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [successData, setSuccessData] = useState(null);

  if (!wallet || !baitcashAccount) {
    navigate("/dashboard");
    return null;
  }

  const goBack = () => navigate("/dashboard");

  const formatPkr = (value) => {
    if (!Number.isFinite(Number(value))) return "₨ 0.00";
    return `₨ ${Number(value).toLocaleString("en-PK", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setCvFile(file);
      message.success(`File "${file.name}" uploaded successfully`);
    }
  };

  const handleSubmit = async () => {
    setError("");

    if (!reasonForFunds || !reasonForFunds.trim()) {
      setError("Please provide a reason for the funds.");
      return;
    }
    if (!profession || !profession.trim()) {
      setError("Please enter your profession.");
      return;
    }
    if (!amount || Number(amount) <= 0) {
      setError("Please enter a valid amount.");
      return;
    }
    if (Number(amount) > Number(baitcashBalance || 0)) {
      setError("Insufficient BAIT Cash balance.");
      return;
    }
    if (!cvFile) {
      setError("Please upload your CV/Resume.");
      return;
    }

    setSubmitting(true);
    try {
      // Simulate API call – replace with actual endpoint
      await new Promise((resolve) => setTimeout(resolve, 1500));

      const applicationData = {
        reason: reasonForFunds,
        profession,
        amountPKR: amount,
        fileName: cvFile.name,
        status: "Pending Review",
        timestamp: new Date().toISOString(),
      };

      setSuccessData(applicationData);
      setSuccess(true);
      message.success("Application submitted successfully!");
    } catch (err) {
      setError(
        err?.message || "Application failed. Please try again."
      );
      message.error("Application failed.");
    } finally {
      setSubmitting(false);
    }
  };

  // ==========================================================
  // STYLES
  // ==========================================================
  const styles = {
    page: {
      width: "100%",
      minHeight: "80vh",
      background: "#faf8f5",
      boxSizing: "border-box",
      display: "flex",
      flexDirection: "column",
      overflowX: "hidden",
    },

    container: {
      width: "calc(100% - 40px)",
      maxWidth: 540,
      margin: "0 auto 32px",
      padding: "0 0 16px",
      boxSizing: "border-box",
    },

    pageTitle: {
      width: "100%",
      marginBottom: 24,
      boxSizing: "border-box",
    },
    titleWrapper: {
      position: "relative",
      width: "100%",
      minHeight: 52,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      boxSizing: "border-box",
    },
    backButton: {
      position: "absolute",
      left: 0,
      top: "50%",
      transform: "translateY(-50%)",
      width: 36,
      height: 36,
      padding: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "rgba(154,106,24,0.06)",
      border: "1px solid rgba(154,106,24,0.12)",
      borderRadius: "10px",
      color: "#9a6a18",
      cursor: "pointer",
      transition: "all 0.2s ease",
      boxSizing: "border-box",
      flexShrink: 0,
    },
    titleContent: {
      width: "100%",
      textAlign: "center",
      padding: "0 48px",
      boxSizing: "border-box",
    },
        title: {
      margin: "0 0 5px",

      fontFamily:
        "'Inter', 'Poppins', 'Segoe UI', sans-serif",

      fontSize: 26,

      lineHeight: 1.1,

      fontWeight: 600,

      letterSpacing: "1.4px",

      color: "#8a5a1f",

      WebkitTextStroke: "0.4px #6b3d0a",

      textShadow:
        "0 1px 0 rgba(255, 240, 200, 0.6), 0 2px 6px rgba(154, 106, 24, 0.22)",
    },
    subtitle: {
      margin: "4px 0 0",

      fontFamily:
        "'Poppins', 'Segoe UI', sans-serif",

      fontSize: 11,

      fontWeight: 500,

      letterSpacing: "0.6px",

      lineHeight: 1.3,

      color: "#a07a3f",

      opacity: 0.95,
    },

    formContainer: { width: "100%", boxSizing: "border-box" },
    successContainer: {
      width: "100%",
      boxSizing: "border-box",
      textAlign: "center",
    },

    errorBox: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      background: "#fef3e8",
      color: "#9a5b12",
      border: "1px solid #f5d9b8",
      borderRadius: "10px",
      padding: "8px 12px",
      marginBottom: "16px",
      fontSize: "12px",
      fontWeight: 500,
    },

    balanceCard: {
      background: "#eee3d4",
      borderRadius: "16px",
      padding: "20px 24px",
      marginBottom: "24px",
      border: "1px solid #f0ba5d",
      boxShadow: "0 4px 16px rgba(0,0,0,0.03)",
    },
    balanceLabel: {
      fontSize: "12px",
      fontWeight: 600,
      color: "#92703d",
      letterSpacing: "0.3px",
      textTransform: "uppercase",
      marginBottom: "4px",
    },
    balanceAmount: {
      fontSize: "32px",
      fontWeight: 800,
      color: "#2a1f12",
      margin: "2px 0 6px",
      letterSpacing: "-0.5px",
    },
    balanceUser: {
      fontSize: "13px",
      color: "#8a7a62",
      display: "flex",
      alignItems: "center",
      gap: "6px",
    },

    card: {
      background: "#ffffff",
      borderRadius: "16px",
      padding: "24px",
      border: "1px solid #ece4d6",
      boxShadow: "0 4px 16px rgba(0,0,0,0.03)",
      marginBottom: "20px",
    },

    formSection: {
      marginBottom: "20px",
    },
    formLabel: {
      display: "block",
      fontSize: "13px",
      fontWeight: 600,
      color: "#5a4a32",
      marginBottom: "6px",
    },
    formTextarea: {
      width: "100%",
      minHeight: "80px",
      padding: "10px 14px",
      borderRadius: "10px",
      border: "1px solid #e0d6c8",
      fontSize: "15px",
      fontWeight: 500,
      color: "#2a1f12",
      background: "#ffffff",
      transition: "border-color 0.2s, box-shadow 0.2s",
      boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
      outline: "none",
      fontFamily: "inherit",
      resize: "vertical",
    },
    formInput: {
      width: "100%",
      height: "48px",
      padding: "0 14px",
      borderRadius: "10px",
      border: "1px solid #e0d6c8",
      fontSize: "15px",
      fontWeight: 500,
      color: "#2a1f12",
      background: "#ffffff",
      transition: "border-color 0.2s, box-shadow 0.2s",
      boxShadow: "0 2px 4px rgba(0,0,0,0.02)",
      outline: "none",
    },
    fileInput: {
      width: "100%",
      padding: "10px 14px",
      borderRadius: "10px",
      border: "1px solid #e0d6c8",
      fontSize: "14px",
      color: "#2a1f12",
      background: "#ffffff",
      outline: "none",
    },
    formHelper: {
      marginTop: "4px",
      fontSize: "11px",
      color: "#9b8b73",
      lineHeight: 1.4,
    },

    submitButton: {
      width: "100%",
      height: "52px",
      borderRadius: "12px",
      border: "none",
      background: "linear-gradient(135deg, #9a6a18, #c18a32)",
      color: "#ffffff",
      fontSize: "15px",
      fontWeight: 700,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      cursor: "pointer",
      transition: "all 0.2s ease",
      marginTop: "8px",
      boxShadow: "0 4px 16px rgba(154,106,24,0.18)",
    },

    securityNote: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      marginTop: "24px",
      padding: "12px 0",
      borderTop: "1px solid #ece4d6",
      fontSize: "12px",
      color: "#9b8b73",
    },
    securityIcon: { color: "#9a6a18" },

    successIcon: { color: "#4cae5b" },
    successTitle: {
      margin: "14px 0 2px",
      fontSize: "22px",
      fontWeight: 700,
      color: "#2a1f12",
    },
    successSubtitle: { fontSize: "13px", color: "#8a7a62" },
    successCard: {
      background: "#f8f4ec",
      borderRadius: "12px",
      padding: "14px 16px",
      margin: "10px 0",
      border: "1px solid #ece4d6",
      textAlign: "left",
    },
    successRow: {
      display: "flex",
      justifyContent: "space-between",
      padding: "6px 0",
      fontSize: "13px",
      color: "#5a4a32",
      borderBottom: "1px solid #ece4d6",
    },
  };

  // ==========================================================
  // RENDER
  // ==========================================================
  return (
    <div style={styles.page}>
      {/* Main content */}
      <div style={styles.container}>
        <div style={styles.pageTitle}>
          <div style={styles.titleWrapper}>
            {/* BACK BUTTON — uses shared .market-back-btn styles */}
        <button
          type="button"
          className="market-back-btn"
          onClick={goBack}
          title="Back to Dashboard"
          aria-label="Back to Dashboard"
        >
          <ArrowLeftIcon width={18} height={18} />
        </button>
            <div style={styles.titleContent}>
              <h1 style={styles.title}>
                Women Empowerment Fund
              </h1>
              <p style={styles.subtitle}>
                Apply for funds to support your empowerment journey
              </p>
            </div>
          </div>
        </div>

        {success ? (
          // ---------- SUCCESS ----------
          <div style={styles.successContainer}>
            <div style={styles.successIcon}>
              <CheckCircleIcon width={60} height={60} />
            </div>
            <h2 style={styles.successTitle}>
              Application Submitted
            </h2>
            <p style={styles.successSubtitle}>
              Your application is being reviewed.
            </p>
            <div style={styles.successCard}>
              <div style={styles.successRow}>
                <span>Reason</span>
                <strong>{successData?.reason}</strong>
              </div>
              <div style={styles.successRow}>
                <span>Profession</span>
                <strong>{successData?.profession}</strong>
              </div>
              <div style={styles.successRow}>
                <span>Amount Requested</span>
                <strong>
                  {formatPkr(successData?.amountPKR)}
                </strong>
              </div>
              <div style={styles.successRow}>
                <span>CV/Resume</span>
                <strong>{successData?.fileName}</strong>
              </div>
              <div style={styles.successRow}>
                <span>Status</span>
                <strong style={{ color: "#f59e0b" }}>
                  {successData?.status}
                </strong>
              </div>
            </div>
            <button onClick={goBack} style={styles.submitButton}>
              Done
            </button>
          </div>
        ) : (
          // ---------- FORM ----------
          <div style={styles.formContainer}>
            {error && (
              <div style={styles.errorBox}>
                <InformationCircleIcon width={18} height={18} />
                <span>{error}</span>
              </div>
            )}

            {/* Balance Card */}
            <div style={styles.balanceCard}>
              <div style={styles.balanceLabel}>
                Available BAIT Cash
              </div>
              <div style={styles.balanceAmount}>
                {formatPkr(baitcashBalance || 0)}
              </div>
              {baitcashAccount?.phoneNumber && (
                <div style={styles.balanceUser}>
                  <UserIcon width={14} height={14} />
                  {baitcashAccount.fullName} •{" "}
                  {baitcashAccount.phoneNumber}
                </div>
              )}
            </div>

            <div style={styles.card}>
              <div style={styles.formSection}>
                <label style={styles.formLabel}>
                  Reason for Funds
                </label>
                <textarea
                  value={reasonForFunds}
                  onChange={(e) => {
                    setReasonForFunds(e.target.value);
                    setError("");
                  }}
                  placeholder="Please provide a detailed reason for applying"
                  disabled={submitting}
                  style={styles.formTextarea}
                  rows={3}
                />
                <div style={styles.formHelper}>
                  Explain why you need the funds
                </div>
              </div>

              <div style={styles.formSection}>
                <label style={styles.formLabel}>Profession</label>
                <input
                  type="text"
                  value={profession}
                  onChange={(e) => {
                    setProfession(e.target.value);
                    setError("");
                  }}
                  placeholder="Enter your profession"
                  disabled={submitting}
                  style={styles.formInput}
                />
                <div style={styles.formHelper}>
                  Your current profession or field of work
                </div>
              </div>

              <div style={styles.formSection}>
                <label style={styles.formLabel}>
                  Amount Requested (PKR)
                </label>
                <div style={{ position: "relative" }}>
                  <input
                    type="text"
                    inputMode="decimal"
                    value={amount}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (
                        value === "" ||
                        /^\d*(\.\d{1,2})?$/.test(value)
                      ) {
                        setAmount(value);
                        setError("");
                      }
                    }}
                    placeholder="0.00"
                    disabled={submitting}
                    style={styles.formInput}
                  />
                  <span
                    style={{
                      position: "absolute",
                      right: "16px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#8a7a62",
                    }}
                  >
                    PKR
                  </span>
                </div>
                <div style={styles.formHelper}>
                  Enter the amount you are requesting
                </div>
              </div>

              <div style={styles.formSection}>
                <label style={styles.formLabel}>
                  Upload CV/Resume
                </label>
                <input
                  type="file"
                  accept=".pdf, .doc, .docx"
                  onChange={handleFileUpload}
                  disabled={submitting}
                  style={styles.fileInput}
                />
                {cvFile && (
                  <div style={styles.formHelper}>
                    <DocumentTextIcon
                      width={14}
                      height={14}
                      style={{
                        display: "inline",
                        color: "#9a6a18",
                      }}
                    />
                    {cvFile.name} (
                    {(cvFile.size / 1024).toFixed(1)} KB)
                  </div>
                )}
                <div style={styles.formHelper}>
                  Accepted formats: PDF, DOC, DOCX
                </div>
              </div>
            </div>

            <button
              onClick={handleSubmit}
              disabled={
                submitting ||
                !reasonForFunds ||
                !profession ||
                !amount ||
                !cvFile
              }
              style={styles.submitButton}
            >
              {submitting ? (
                <>
                  <Spin size="small" style={{ marginRight: 8 }} />
                  Submitting...
                </>
              ) : (
                <>
                  Submit Application
                  <ArrowRightIcon width={18} height={18} />
                </>
              )}
            </button>

            <div style={styles.securityNote}>
              <LockClosedIcon
                width={14}
                height={14}
                style={styles.securityIcon}
              />
              <span>Secure • Confidential • 24/7</span>
              <span style={{ marginLeft: "auto", opacity: 0.7 }}>
                Your application is handled with care and privacy
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WomenFundPage;